import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation  } from 'react-router-dom';
import useFetch from '../../hooks/useFetch';

import Tube from './Tube';

function ApplauseGame() {
  const authState = useSelector(state => state.authReducer);
  const [fetchData] = useFetch();
  const navigate = useNavigate();
  const location = useLocation();
  const [userId, setUserId] = useState(authState.user._id); 
  const [userCode, setUserCode] = useState(authState.user.think5whyscode); 
  const [userName, setUserName] = useState(authState.user.name); 
  const [userEmail, setUserEmail] = useState(authState.user.email); 
  const [userPhone, setUserPhone] = useState(''); 

  const [creditPoints, setCreditPoints] = useState(20);

  const levelConfigurations = [
    // Level 1 - Simple Setup
    [
      [1, 2, 2, 1],
      [3, 3, 4, 4],
      [1, 1, 3, 3],
      [4, 4, 2, 2],
      []
    ],
    // Level 2 - More Complex Setup
    [
      [1, 2, 3, 4],
      [4, 3, 2, 1],
      [1, 3, 4, 2],
      [2, 4, 1, 3],
      [3, 1, 2, 4],
      [],
      []
    ],
    // Level 3 - Advanced Setup
    [
      [1, 4, 3, 2],
      [3, 2, 1, 4],
      [2, 1, 4, 3],
      [4, 3, 2, 1],
      [3, 4, 1, 2],
      []
    ]
  ];

  const [tubes, setTubes] = useState(levelConfigurations[0]); // Default to Level 1 setup
  const [selectedTube, setSelectedTube] = useState(null);
  const [moves, setMoves] = useState(0);
  const [time, setTime] = useState(0);
  const [isWin, setIsWin] = useState(false);
  const [level, setLevel] = useState(1);
  const [previousLevels, setPreviousLevels] = useState([]);
  
  const timerRef = useRef(null);

  useEffect(() => {
    // Start the timer
    timerRef.current = setInterval(() => setTime(prevTime => prevTime + 1), 1000);
    return () => clearInterval(timerRef.current);
  }, []);

  useEffect(() => {
    if (checkWin()) {
      setIsWin(true);
    }
  }, [tubes]);

  useEffect(() => {
    if (isWin) {
      clearInterval(timerRef.current);
    }
  }, [isWin]);

  const handleTubeClick = (index) => {
    if (selectedTube === null) {
      setSelectedTube(index);
    } else {
      if (selectedTube !== index) {
        moveItem(selectedTube, index);
        setMoves(moves + 1);
      }
      setSelectedTube(null);
    }
  };

  const moveItem = (fromIndex, toIndex) => {
    const newTubes = [...tubes];
    const fromTube = newTubes[fromIndex];
    const toTube = newTubes[toIndex];

    if (
      fromTube.length > 0 &&
      (toTube.length === 0 || fromTube[fromTube.length - 1] === toTube[toTube.length - 1]) &&
      toTube.length < 4
    ) {
      toTube.push(fromTube.pop());
      setTubes(newTubes);
    }
  };

  // Check if the player has completed the puzzle
  const checkWin = () => {
    return tubes.every(tube => {
      // A tube is "won" if it is either empty or contains only one color
      return tube.length === 0 || tube.every(ball => ball === tube[0]);
    });
  };

  const resetGame = () => {
    setTubes(levelConfigurations[level - 1]); // Reset to current level's configuration
    setMoves(0);
    setTime(0);
    setIsWin(false);
    setSelectedTube(null);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setTime(prevTime => prevTime + 1), 1000);
  };

  const handleNextLevel = () => {
    if (level < 3) {
      setPreviousLevels(prevLevels => [
        ...prevLevels,
        { level, moves, time }
      ]);
      setLevel(level + 1);
      resetGame();
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

return (
    <div className="game-board flex flex-col items-center justify-center min-h-screen bg-gray-800 px-4 py-8">
      {/* Header Section */}
      <div className="header flex flex-col items-center mb-8 text-center">
        <p className="text-2xl font-semibold text-white mb-2">Think5Whys Applause Puzzle</p>
        <p className="text-m text-yellow-200">
          You need a minimum of 10 Credit Points in your Think5Whys Game Purse to play this puzzle!
        </p>
        <p className="text-m text-white">
          You can withdraw real money from your Think5Whys Game Purse into your bank account (only via UPI) once you reach 500 Credit Points.
        </p>
        <p className="text-m text-yellow-200">
          Currently, you have {creditPoints} in your Think5Whys Game Purse.
        </p>
      </div>
  
      {/* Current Level Section */}
      <h2 className="text-lg text-white">
        Current Level: {`Level ${level}`}
      </h2>
  
      {/* Previous Level Stats Section */}
      {previousLevels.length > 0 && (
        <div className="previous-level-stats mb-2">
          <h3 className="text-lg text-white mb-2">Previous Levels Stats</h3>
          <div className="space-y-2">
            {previousLevels.map((levelData, index) => (
              <p key={index} className="text-lg text-white">
                Level {levelData.level} - Moves: {levelData.moves}, Time: {levelData.time} seconds
              </p>
            ))}
          </div>
        </div>
      )}
  
      {/* Stats Section (Moves & Time) */}
      <div className="stats text-m text-white mb-2">
        <p>Moves: {moves} {' '}Time: {time} seconds</p>
      </div>
  
      <div className="tubes-container flex flex-wrap justify-center items-center gap-4 mb-2">
        {tubes.map((tube, index) => (
            <Tube
            key={index}
            items={tube}
            isSelected={index === selectedTube}
            onClick={() => handleTubeClick(index)}
            />
        ))}
      </div>

  
      {/* Win Message Section */}
      {isWin && (
        <p className="text-lg text-yellow-200 mb-2">
          Congratulations! You solved the puzzle in {time} seconds and {moves} moves!
        </p>
      )}
  
      {/* Action Buttons */}
      <div className="buttons flex flex-col sm:flex-row gap-4 items-center sm:justify-center mt-2">
        <button
          onClick={resetGame}
          className="px-6 py-3 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-all"
        >
          Restart Game
        </button>
  
        <button
          onClick={handleNextLevel}
          className={`px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-all ${isWin ? "" : "opacity-50 cursor-not-allowed"}`}
          disabled={!isWin}
        >
          Next Level
        </button>

        <button
          onClick={handleBack}
          className="px-6 py-3 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-all"
        >
          Back
        </button>
      </div>
    </div>
  );
  
}

export default ApplauseGame;
